/**
 * 숫자·날짜 포맷 유틸리티.
 *
 * 중요: 이 파일의 출력은 백엔드 MoneyUtils.java와 정확히 일치해야 한다.
 * 같은 값이 화면 A에서는 "1,235원", 화면 B에서는 "1,234.56원"으로 보이면
 * 사용자는 어느 쪽을 믿어야 할지 알 수 없다.
 * 대응하는 테스트가 format.test.ts에 있고, 기대값은 Java 쪽과 동일하게 박아두었다.
 *
 * 새 파일에서 숫자·날짜 포맷을 직접 구현하지 말고 여기 것을 import 한다.
 * 없으면 여기에 추가하고 쓴다. (CLAUDE.md 5.2절)
 *
 * 정밀도에 대해
 *   백엔드는 금액을 BigDecimal로 다루지만 JSON을 거치면 JS number(double)가 된다.
 *   double은 정수부 2^53(약 9000조)까지 정확하므로 개인 자산 규모에서는 문제가 없다.
 *   다만 프론트에서 금액을 더하거나 곱해 새 값을 만들지는 않는다.
 *   계산은 백엔드가 하고 프론트는 받은 값을 표시만 한다.
 */

/** 값이 없을 때 화면에 표시할 문자열. Java MoneyUtils와 동일하다. */
const EMPTY = '-';

/**
 * HALF_UP 반올림. 0.5는 항상 0에서 먼 쪽으로 보낸다.
 *
 *   roundHalfUp(2.5, 0)  →  3
 *   roundHalfUp(-2.5, 0) → -3
 *
 * JS의 Math.round를 그냥 쓰면 안 되는 이유
 *   Math.round는 항상 +무한대 방향으로 올린다.
 *   Math.round(-2.5) === -2  ← Java의 HALF_UP은 -3
 *   음수 수익률(손실)에서 백엔드와 값이 갈린다.
 *
 * 지수 표기 문자열을 거치는 이유
 *   1234.565 * 100 은 부동소수점 오차로 123456.49999...가 되어 내림된다.
 *   Number('1234.565e2')는 문자열 파싱이라 123456.5로 정확히 나온다.
 */
export function roundHalfUp(value: number, scale = 0): number {
  if (!Number.isFinite(value)) {
    return value;
  }
  const sign = value < 0 ? -1 : 1;
  const abs = Math.abs(value);

  const shifted = Number(`${abs}e${scale}`);
  const rounded = Math.round(shifted);

  return sign * Number(`${rounded}e-${scale}`);
}

/**
 * 원화 형식으로 포맷한다. 원 단위로 반올림하고 천 단위 구분 기호를 넣는다.
 *
 *   formatKrw(1234567) → "1,234,567원"
 *   formatKrw(1234.56) → "1,235원"
 *   formatKrw(-5000)   → "-5,000원"
 *   formatKrw(null)    → "-"
 */
export function formatKrw(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY;
  }
  const won = roundHalfUp(value, 0);
  // Intl은 -0을 "-0"으로 찍는다. 0으로 정규화한다.
  return `${(won === 0 ? 0 : won).toLocaleString('ko-KR')}원`;
}

/**
 * 미국 주가 표기. 예: 1234.5 → "$1,234.50" (2026-08-20, 미국 지원)
 *
 * 🔴 formatUsd(AI 비용, 소수 4자리)와 다른 함수다
 *   주가는 센트(2자리)가 단위다. 비용 포맷터를 그대로 쓰면 $63.0000 이 된다.
 *
 * 🔴 백엔드 MoneyUtils.formatUsdPrice 와 출력이 같아야 한다 (CLAUDE.md 6.1절)
 *   기대값은 MoneyUtilsTest 와 동일하게 format.test.ts 에 박아둔다.
 *   음수 부호는 formatUsd 와 같이 통화기호 앞이다: "-$12.35"
 */
export function formatUsdPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY;
  }
  const cents = roundHalfUp(value, 2);
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${Math.abs(cents === 0 ? 0 : cents).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * 백분율 형식으로 포맷한다. 양수에는 + 부호를 붙여 등락을 드러낸다.
 *
 *   formatPercent(15)    → "+15.00%"
 *   formatPercent(-3.25) → "-3.25%"
 *   formatPercent(0)     → "0.00%"
 *   formatPercent(null)  → "-"
 *
 * 0에 부호를 붙이지 않는 것도 백엔드와 맞춘 동작이다. "+0.00%"는 어색하다.
 */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY;
  }
  const rounded = roundHalfUp(value, 2);
  const sign = rounded > 0 ? '+' : '';
  const body = (rounded === 0 ? 0 : rounded).toLocaleString('ko-KR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}${body}%`;
}

/**
 * 부호를 항상 붙여 숫자를 포맷한다. 손익 금액 표시 등에 쓴다.
 *
 *   formatSignedNumber(1500)  → "+1,500"
 *   formatSignedNumber(-1500) → "-1,500"
 *   formatSignedNumber(0)     → "0"
 */
export function formatSignedNumber(
  value: number | null | undefined,
  fractionDigits = 0,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY;
  }
  const rounded = roundHalfUp(value, fractionDigits);
  const sign = rounded > 0 ? '+' : '';
  const body = (rounded === 0 ? 0 : rounded).toLocaleString('ko-KR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return `${sign}${body}`;
}

/**
 * 달러 금액을 포맷한다. AI 비용에만 쓴다.
 *
 *   formatUsd(0.00494) → "$0.0049"
 *   formatUsd(19.5)    → "$19.5000"
 *   formatUsd(null)    → "-"
 *
 * 왜 원화로 환산하지 않나
 *   Anthropic 요금이 USD 로 청구된다. 원화로 바꾸면 환율 기준일을 정해야 하고,
 *   그 값이 화면과 실제 카드 청구액 사이의 새로운 오차가 된다.
 *
 * 왜 소수 4자리인가
 *   호출 한 건이 $0.005 수준이다. 2자리로 자르면 개별 호출이 전부 $0.00 이 되어
 *   "얼마 안 나갔네" 로 읽힌다. 실제로는 100번이면 $0.5 다.
 *
 * roundHalfUp 을 쓰는 이유는 다른 포맷터와 같다 — JS Math.round 는
 * 음수 0.5 에서 Java HALF_UP 과 갈린다. (CLAUDE.md 6.1절)
 */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY;
  }
  const rounded = roundHalfUp(value, 4);

  // 음수 부호는 통화기호 앞에 둔다.
  // toLocaleString 에 그대로 맡기면 "$-0.0001" 이 되어 읽기 어색하다.
  // 비용이 음수가 될 일은 없지만, 포맷터가 어떤 입력에서도 말이 되게 해둔다.
  const sign = rounded < 0 ? '-' : '';
  return `${sign}$${Math.abs(rounded === 0 ? 0 : rounded).toLocaleString('en-US', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })}`;
}

/**
 * 큰 금액을 축약해 표시한다. 좁은 카드나 차트 축에 쓴다.
 *
 *   formatKrwCompact(1234)      → "1,234원"
 *   formatKrwCompact(12345678)  → "1,235만원"
 *   formatKrwCompact(123456789) → "1.2억원"
 *
 * 정확한 금액이 필요한 곳(잔고, 거래 상세)에는 쓰지 않는다. formatKrw를 쓴다.
 */
export function formatKrwCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY;
  }
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs >= 100_000_000) {
    return `${sign}${roundHalfUp(abs / 100_000_000, 1).toLocaleString('ko-KR')}억원`;
  }
  if (abs >= 10_000) {
    return `${sign}${roundHalfUp(abs / 10_000, 0).toLocaleString('ko-KR')}만원`;
  }
  return formatKrw(value);
}

/**
 * 날짜를 yyyy-MM-dd 로 포맷한다. 백엔드 DateUtils.format(LocalDate)와 같은 형식이다.
 *
 * 입력은 백엔드가 내려준 ISO 문자열("2026-08-09" 또는 "2026-08-09T16:30:00")이거나 Date다.
 *
 * 시간대 주의
 *   "2026-08-09" 같은 날짜만 있는 문자열을 new Date()에 넣으면 UTC 자정으로 해석되어
 *   한국(UTC+9)에서 하루 밀릴 수 있다. 그래서 날짜만 있는 문자열은 파싱하지 않고
 *   앞 10글자를 그대로 쓴다.
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return EMPTY;
  }
  if (typeof value === 'string') {
    // "2026-08-09" 또는 "2026-08-09T16:30:00" 둘 다 앞 10글자가 날짜다.
    const head = value.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : EMPTY;
  }
  if (Number.isNaN(value.getTime())) {
    return EMPTY;
  }
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * 날짜와 시각을 yyyy-MM-dd HH:mm 으로 포맷한다.
 *
 * 백엔드가 KST 기준 LocalDateTime을 시간대 정보 없이 내려주므로
 * (예: "2026-08-09T16:30:00") 브라우저 시간대로 변환하지 않고 문자열을 그대로 쓴다.
 * new Date()로 파싱하면 브라우저가 로컬 시간대로 해석해 값이 틀어진다.
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return EMPTY;
  }
  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(value);
  return match ? `${match[1]} ${match[2]}` : EMPTY;
}

/**
 * 그 시각이 얼마나 지났는지 — "방금 · n분 전 · n시간 전 · n일 전".
 * (2026-10-16 admin/batchFormat.ts 에서 옮겼다 — 배치 화면 "마지막 실행" 과 계좌 화면 "마지막 동기화" 가 함께 쓴다)
 *
 * 절대 시각만 보여주면 "07:00 에 돌았다" 를 읽고도 그게 오늘인지
 * 사흘 전인지 바로 알기 어렵다.
 *
 * @param now 테스트에서 시각을 고정하려고 받는다. 화면은 넘기지 않는다
 */
export function formatSince(startedAt: string, now: Date = new Date()): string {
  const elapsedMs = now.getTime() - new Date(startedAt).getTime();

  if (elapsedMs < 0) {
    // 서버와 브라우저의 시계가 조금 어긋난 경우다. 음수를 그대로 보여주지 않는다.
    return '방금';
  }

  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) {
    return '방금';
  }
  if (minutes < 60) {
    return `${minutes}분 전`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}시간 전`;
  }
  return `${Math.floor(hours / 24)}일 전`;
}

/**
 * 등락에 따른 Tailwind 클래스를 반환한다.
 *
 * 한국 시장 관행: 상승 빨강, 하락 파랑. (미국은 반대)
 * 색을 각 컴포넌트에서 직접 고르면 언젠가 반대로 쓰는 곳이 생긴다.
 *
 * 셋 다 index.css 의 의미 토큰이다. 하드코딩된 색이 아니다.
 * 다크 모드에서 값만 바뀐다 — 어두운 배경에서 파랑(#2563eb)은 거의 안 보인다.
 *
 * 🔴 0·null 경로가 2026-08-16 테마 전환에서 누락됐다 (세션 B 가 발견)
 *   `text-slate-500` 이 남아 있었다. 다크 모드에서 이 자리만 어둡게 남는다.
 *   sweep 을 `--include=*.tsx` 로만 돌려 `.ts` 파일을 아예 보지 않은 것이 원인이다.
 *   값이 정확히 0 일 때만 타는 경로라 눈으로는 잡기 어렵다 —
 *   가계부 순액이 0 인 달에 실제로 탄다.
 */
export function changeColorClass(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value) || value === 0) {
    return 'text-fg-muted';
  }
  return value > 0 ? 'text-rise' : 'text-fall';
}

/**
 * 금액 입력칸에 보여줄 문자열. 콤마를 넣는다.
 *
 * formatKrw 와 다른 점
 *   "원" 을 붙이지 않는다. 입력칸에 단위가 붙어 있으면 커서를 그 뒤로 옮길 수 있고,
 *   지우다가 "10000" 이 아니라 "10000원" 을 넘기게 된다.
 *   단위는 입력칸 밖에 라벨이나 접미사로 둔다.
 *
 * 입력 중인 값을 다루므로 숫자가 아니라 문자열을 받는다.
 * "1000abc" 처럼 이상한 것이 들어와도 숫자만 남겨 포맷한다.
 *
 *   toMoneyDisplay('1000')     → '1,000'
 *   toMoneyDisplay('1,0a00')   → '1,000'
 *   toMoneyDisplay('')         → ''
 *   toMoneyDisplay('0')        → '0'
 */
export function toMoneyDisplay(raw: string): string {
  const digits = toMoneyValue(raw);
  if (digits === '') {
    return '';
  }
  return Number(digits).toLocaleString('ko-KR');
}

/**
 * 서버로 보낼 값. 콤마와 그 밖의 문자를 전부 뺀 숫자 문자열이다.
 *
 * 🔴 저장할 때는 반드시 이 값을 쓴다
 *   화면에 보이는 '1,000' 을 그대로 보내면 서버에서 숫자로 못 읽는다.
 *
 *   toMoneyValue('1,000')   → '1000'
 *   toMoneyValue('1,0a00')  → '1000'
 *   toMoneyValue('')        → ''
 *
 * 앞자리 0 을 다듬는다 — '007' 은 '7' 이 된다.
 * 다만 '0' 자체는 남긴다. 0 을 입력하는 것이 뜻을 갖는 자리가 있다.
 *
 * 🔴 음수와 소수를 받지 않는다
 *   이 앱에서 사람이 손으로 넣는 금액은 여유자금·납입액처럼 전부 양의 정수다.
 *   음수가 필요한 자리가 생기면 그때 확장한다. 지금 열어두면
 *   '-' 만 남은 상태 같은 중간값을 전부 다뤄야 한다.
 */
export function toMoneyValue(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits === '') {
    return '';
  }
  const trimmed = digits.replace(/^0+/, '');
  return trimmed === '' ? '0' : trimmed;
}

/**
 * 자유 텍스트 안의 큰 숫자에 천단위 구분을 넣는다. (2026-09-02 사용자 요청)
 *
 * 🔴 왜 렌더 시점에 고치나
 *   AI 가 쓴 월간 리포트·회고·종목 분석은 문장 안에 금액이 섞여 있고,
 *   모델이 "2237174원" 처럼 콤마 없이 쓰는 일이 잦다. 프롬프트로도 요청하지만
 *   그것만으로는 이미 저장된 글이 안 고쳐지고, 모델이 한 번씩 빠뜨리기도 한다.
 *   화면에 나오는 금액은 예외 없이 자릿수가 끊겨 있어야 한다는 규칙을
 *   출력 직전에 한 번 더 지키는 그물이다.
 *
 * 무엇을 바꾸나 — 네 자리 이상의 숫자 덩어리
 *   세 자리 이하는 건드리지 않는다. "3개", "12건" 에 콤마가 붙으면 이상하다.
 *
 * 무엇을 건드리지 않나
 *   앞에 숫자·콤마·마침표가 붙은 것            12.5, 1,234
 *   뒤에 숫자·콤마·마침표·하이픈이 붙은 것     2026-08, 12.5, 1,234
 *     → 날짜·소수·이미 서식이 된 값이다. 그래서 이 함수는 여러 번 걸어도
 *       결과가 같다(멱등) — 이미 콤마가 있으면 덩어리가 세 자리로 쪼개져 안 걸린다
 *   연도·기간을 뜻하는 단위가 뒤에 오는 것     2026년, 1500일, 2000회
 *
 * 예
 *   toThousands('지출은 2237174원으로 전월 1765627원보다 늘었다')
 *     → '지출은 2,237,174원으로 전월 1,765,627원보다 늘었다'
 *   toThousands('2026년 8월 지출')  → 그대로 (연도는 금액이 아니다)
 */
export function toThousands(text: string): string {
  /*
    앞 경계를 lookbehind 가 아니라 캡처로 잡는다.
    lookbehind 는 사파리 16.4 미만에서 정규식 파싱 자체가 실패해
    화면이 통째로 죽는다. 폰이 주 사용처라 감수할 수 없다.
  */
  return text.replace(
    /(^|[^\d,.])(\d{4,})(?![\d,.\-])(?!\s*(?:년|월|일|시|분|초|회|개|건|명|위|배|%))/g,
    (_match, before: string, digits: string) =>
      before + Number(digits).toLocaleString('ko-KR'),
  );
}

/**
 * 주식 화면의 가격 표기. 단위를 붙이지 않는다. (2026-09-03 사용자 요청)
 *
 * 🔴 왜 "원" 을 뗐나
 *   증권사 관심종목 화면이 그렇게 한다. 한 열이 통째로 같은 통화라
 *   줄마다 "원" 을 반복하면 자릿수를 읽는 데 방해만 된다.
 *   좁은 폰 화면에서는 그 두 글자가 종목명을 밀어내기까지 했다.
 *
 *   단위는 열 제목이나 카드 제목이 한 번만 말한다.
 *
 * 🔴 formatKrw 를 없애지 않는다
 *   가계부·혜택은 문장 안에 금액이 섞여 나와 단위가 반드시 필요하다.
 *   ("지출 2,237,174원") 주식 표에서만 이것을 쓴다.
 *
 *   formatKrwPlain(1234567) → "1,234,567"
 *   formatKrwPlain(null)    → "-"
 */
export function formatKrwPlain(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY;
  }
  const won = roundHalfUp(value, 0);
  // Intl 은 -0 을 "-0" 으로 찍는다. 0 으로 정규화한다 (formatKrw 와 같은 이유)
  return (won === 0 ? 0 : won).toLocaleString('ko-KR');
}

/**
 * 미국 주가 표기, 통화기호 없음. (2026-09-03)
 *
 * 소수 두 자리는 유지한다 - 센트가 값의 일부라 떼면 다른 값이 된다.
 * 국내와 달리 자리수가 짧아 기호가 자리를 많이 먹지는 않지만,
 * 한 표에 국내·미국이 섞이면 정렬이 어긋나므로 규칙을 맞춘다.
 *
 *   formatUsdPricePlain(1234.5) → "1,234.50"
 */
export function formatUsdPricePlain(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY;
  }
  return roundHalfUp(value, 2).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
